<!-- carrier-work-orders.component.html -->
<app-menu>
  <div class="cwo-shell min-h-full bg-[var(--cwo-bg)] text-[var(--cwo-text-main)]" [attr.data-theme]="themeMode">
    <div class="mx-auto flex h-[calc(100vh-64px)] min-h-[calc(100vh-64px)] min-w-0 max-w-[1800px] flex-col gap-3 px-3 pb-3 pt-3 sm:px-4 lg:px-5">
      <section class="relative overflow-hidden rounded-3xl border border-[var(--cwo-border)] bg-[var(--cwo-surface)] shadow-[0_16px_44px_rgba(2,6,23,0.16)]">
        <div class="pointer-events-none absolute -left-16 top-0 h-36 w-36 rounded-full bg-sky-400/10 blur-3xl"></div>
        <div class="pointer-events-none absolute right-0 top-0 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl"></div>
        <div class="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.06),transparent_35%)]"></div>

        <div class="relative grid gap-3 px-4 py-3 sm:px-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] xl:items-end">
          <div class="min-w-0 space-y-3">
            <h1 class="text-2xl font-black tracking-[-0.04em] text-[var(--cwo-text-main)] sm:text-[30px] sm:leading-9">{{ pageTitle }}</h1>

            <div *ngIf="operationalInsights.length; else noOperationalInsights" class="grid gap-2 sm:grid-cols-3">
              <article
                *ngFor="let insight of operationalInsights"
                class="flex min-h-[72px] items-center justify-between gap-3 rounded-xl border px-3 py-2.5 shadow-sm backdrop-blur"
                [ngClass]="
                  insight.tone === 'rose'
                    ? 'border-rose-500/20 bg-rose-500/10'
                    : insight.tone === 'amber'
                      ? 'border-amber-500/20 bg-amber-500/10'
                      : insight.tone === 'sky'
                        ? 'border-sky-500/20 bg-sky-500/10'
                        : 'border-emerald-500/20 bg-emerald-500/10'
                "
              >
                <div class="min-w-0">
                  <p class="text-[10px] font-semibold uppercase leading-4 tracking-[0.16em] text-[var(--cwo-text-secondary)]">{{ insight.title }}</p>
                  <p class="mt-0.5 text-xl font-black leading-6 tracking-[-0.04em] text-[var(--cwo-text-main)]">{{ insight.value | number }}</p>
                </div>

                <span
                  class="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl text-sm"
                  [ngClass]="
                    insight.tone === 'rose'
                      ? 'bg-rose-500/10 text-rose-500'
                      : insight.tone === 'amber'
                        ? 'bg-amber-500/10 text-amber-500'
                        : insight.tone === 'sky'
                          ? 'bg-sky-500/10 text-sky-500'
                          : 'bg-emerald-500/10 text-emerald-500'
                  "
                >
                  <i class="fa-solid" [ngClass]="insight.icon"></i>
                </span>
              </article>
            </div>

            <ng-template #noOperationalInsights>
              <div class="rounded-xl border border-dashed border-[var(--cwo-border)] bg-[var(--cwo-surface-2)] px-3 py-2.5 text-sm font-semibold text-[var(--cwo-text-main)] shadow-sm">
                {{ hasOperationalCoverage ? 'No follow-up items right now.' : 'No follow-up items available for this view.' }}
              </div>
            </ng-template>
          </div>

          <div class="flex min-w-0 flex-col gap-2">
            <div
              *ngIf="canSelectCarrierScope"
              class="rounded-xl border border-[var(--cwo-border)] bg-[var(--cwo-surface-2)]/90 p-2.5 shadow-sm backdrop-blur"
            >
              <div class="flex min-w-0 items-center justify-between gap-3">
                <label class="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--cwo-text-secondary)]" [attr.for]="identityScope + '-scope-select'">
                  {{ scopeLabel }}
                </label>
                <span class="truncate text-xs font-semibold text-[var(--cwo-text-main)]">{{ selectedCarrierScopeLabel }}</span>
              </div>

              <select
                [id]="identityScope + '-scope-select'"
                class="cwo-scope-select mt-1.5 h-10 w-full rounded-lg border px-3 py-2 text-xs font-semibold"
                [ngModel]="selectedCarrierContactUuid"
                [disabled]="carrierOptionsLoading"
                (ngModelChange)="onCarrierSelectionChange($event)"
              >
                <option value="">Select a {{ scopeNoun }}...</option>
                <option *ngFor="let carrier of carrierScopeOptions" [value]="carrier.contact_uuid">
                  {{ carrier.contact_organization }}
                </option>
              </select>

              <p *ngIf="carrierOptionsError" class="mt-1.5 text-[11px] font-medium text-rose-400">
                {{ carrierOptionsError }}
              </p>
            </div>

            <div class="grid grid-cols-3 gap-2">
              <button
                type="button"
                (click)="resetSavedView()"
                [disabled]="loading"
                class="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-[var(--cwo-border)] bg-[var(--cwo-surface)] px-2.5 text-xs font-semibold text-[var(--cwo-text-main)] shadow-sm transition duration-150 hover:-translate-y-0.5 hover:border-[var(--cwo-border-strong)] hover:bg-[var(--cwo-surface-2)] disabled:translate-y-0 disabled:opacity-60"
              >
                <i class="fa-solid fa-rotate-left text-xs"></i>
                Reset
              </button>

              <button
                type="button"
                (click)="toggleTheme()"
                class="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-[var(--cwo-border)] bg-[var(--cwo-surface)] px-2.5 text-xs font-semibold text-[var(--cwo-text-main)] shadow-sm transition duration-150 hover:-translate-y-0.5 hover:border-[var(--cwo-border-strong)] hover:bg-[var(--cwo-surface-2)]"
              >
                <i class="text-xs" [class]="getThemeIcon()"></i>
                {{ getThemeLabel() }}
              </button>

              <button
                type="button"
                (click)="refreshData()"
                [disabled]="loading || (canSelectCarrierScope && !selectedCarrierContactUuid)"
                class="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-[linear-gradient(135deg,#0ea5e9,#2563eb)] px-2.5 text-xs font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.24)] transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(37,99,235,0.32)] disabled:translate-y-0 disabled:opacity-60"
              >
                <span class="cwo-btn-icon">&#x21bb;</span>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </section>

      <div *ngIf="errorMessage" class="flex items-start gap-3 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-500 shadow-sm">
        <span class="mt-0.5 text-base">&#x26A0;</span>
        <div class="min-w-0 flex-1">
          <p class="font-semibold">Grid load issue</p>
          <p class="mt-1 text-red-500/90">{{ errorMessage }}</p>
        </div>
        <button class="rounded-full p-1 transition hover:bg-red-500/10" (click)="errorMessage = ''">&#x2715;</button>
      </div>

      <div *ngIf="loading" class="flex items-center gap-3 rounded-2xl border border-[var(--cwo-border)] bg-[var(--cwo-surface)] px-4 py-3 text-sm text-[var(--cwo-text-secondary)] shadow-sm">
        <div class="cwo-spinner"></div>
        <span>Loading work orders&hellip;</span>
      </div>

      <div class="cwo-grid-shell flex-col overflow-hidden rounded-[28px] border border-[var(--cwo-border)] bg-[var(--cwo-surface)] shadow-[0_18px_50px_rgba(2,6,23,0.14)]">
        <div class="relative min-h-0 flex-1">
          <div class="pointer-events-none absolute inset-x-0 top-0 z-[1] h-16 bg-[linear-gradient(180deg,rgba(14,165,233,0.06),transparent)]"></div>
          <ag-grid-angular
            [class]="themeClass"
            class="cwo-grid"
            [style.opacity]="loading ? 0.45 : 1"
            [gridOptions]="gridOptions"
            [defaultColDef]="defaultColDef"
            [columnDefs]="columnDefs"
            [rowModelType]="rowModelType"
            [rowData]="rowData"
            (gridReady)="onGridReady($event)"
          ></ag-grid-angular>
        </div>
      </div>
    </div>
  </div>
</app-menu>
